import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ShoppingBag,
  Heart,
  ArrowLeft,
  Search,
  SlidersHorizontal,
  Languages
} from 'lucide-react';
import ContactModal from '../components/ContactModal';
import ProductDetailModal from '../components/ProductDetailModal';
import { useLanguage } from '../LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CollectionPage = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCondition, setSelectedCondition] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load products from API
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      // Convert API format to match the expected format
      const formattedProducts = response.data.map(p => {
        const firstImageId = p.images && p.images.length > 0 ? p.images[0] : null;
        
        return {
          id: p.id,
          name: p.title,
          category: p.category,
          price: p.price,
          condition: p.condition,
          description: p.description,
          // Always use thumbnail endpoint for faster loading
          image: firstImageId 
            ? `${BACKEND_URL}/api/uploads/products/${firstImageId}/thumbnail`
            : null,
          // Keep full-size images for detail modal
          images: p.images && p.images.length > 0
            ? p.images.map(img => `${BACKEND_URL}/api/uploads/products/${img}`)
            : [],
          featured: false,
          source: 'database'
        };
      });
      setProducts(formattedProducts);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Read search query from URL on mount
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(products.map(p => p.category));
    return ['all', ...Array.from(cats)].sort();
  }, [products]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Condition filter
    if (selectedCondition !== 'all') {
      filtered = filtered.filter(product => product.condition === selectedCondition);
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'featured':
      default:
        filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
    }

    return filtered;
  }, [products, searchQuery, selectedCategory, selectedCondition, sortBy]);

  const handleAddToWishlist = (productId) => {
    console.log('Added to wishlist:', productId);
    alert('Added to wishlist! (Mock: No backend yet)');
  };

  return (
    <div className="collection-page min-h-screen" style={{ backgroundColor: 'var(--cream-white)' }}>
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ShoppingBag className="w-8 h-8" style={{ color: 'var(--lv-brown)' }} />
            <div>
              <h1 
                className="text-2xl font-bold cursor-pointer" 
                style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
                onClick={() => navigate('/')}
              >
                Love Luxe
              </h1>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                Second-Hand Luxury
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              style={{ color: 'var(--charcoal)' }}
              title={language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
            >
              <Languages className="w-5 h-5" />
              <span className="text-sm font-medium uppercase">{language === 'de' ? 'EN' : 'DE'}</span>
            </button>
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
              style={{ borderColor: 'var(--warm-gray)', color: 'var(--charcoal)' }}
            >
              <ArrowLeft className="w-4 h-4" />
              {t.nav.backToHome}
            </Button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="text-center mb-12 animate-fade-in-up">
            <h1 
              className="text-4xl md:text-5xl font-light mb-4"
              style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
            >
              {t.collectionPage.title}
            </h1>
            <p className="text-lg" style={{ color: 'var(--warm-gray)' }}>
              {filteredProducts.length} {t.collectionPage.itemsAvailable}
            </p>
          </div>

          {/* Filters & Search */}
          <div className="mb-10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--warm-gray)' }} />
                  <Input
                    type="text"
                    placeholder={t.collectionPage.searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    style={{ 
                      backgroundColor: 'white',
                      border: '1px solid var(--soft-taupe)',
                      borderRadius: '8px'
                    }}
                  />
                </div>
              </div>

              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger style={{ backgroundColor: 'white', border: '1px solid var(--soft-taupe)' }}>
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat === 'all' ? t.collectionPage.allCategories : cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger style={{ backgroundColor: 'white', border: '1px solid var(--soft-taupe)' }}>
                  <SelectValue placeholder={t.collectionPage.sortBy} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">{t.collectionPage.featuredFirst}</SelectItem>
                  <SelectItem value="price-low">{t.collectionPage.priceLowHigh}</SelectItem>
                  <SelectItem value="price-high">{t.collectionPage.priceHighLow}</SelectItem>
                  <SelectItem value="name">{t.collectionPage.nameAZ}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Condition Filter Pills */}
            <div className="flex flex-wrap gap-2">
              <Badge
                className="cursor-pointer"
                style={{
                  backgroundColor: selectedCondition === 'all' ? 'var(--warm-sage)' : 'var(--soft-gray)',
                  color: selectedCondition === 'all' ? 'var(--cream-white)' : 'var(--warm-gray)',
                  padding: '8px 16px'
                }}
                onClick={() => setSelectedCondition('all')}
              >
                {t.collectionPage.allConditions}
              </Badge>
              <Badge
                className="cursor-pointer"
                style={{
                  backgroundColor: selectedCondition === 'Excellent' ? 'var(--warm-sage)' : 'var(--soft-gray)',
                  color: selectedCondition === 'Excellent' ? 'var(--cream-white)' : 'var(--warm-gray)',
                  padding: '8px 16px'
                }}
                onClick={() => setSelectedCondition('Excellent')}
              >
                {t.collectionPage.excellent}
              </Badge>
              <Badge
                className="cursor-pointer"
                style={{
                  backgroundColor: selectedCondition === 'Very Good' ? 'var(--warm-sage)' : 'var(--soft-gray)',
                  color: selectedCondition === 'Very Good' ? 'var(--cream-white)' : 'var(--warm-gray)',
                  padding: '8px 16px'
                }}
                onClick={() => setSelectedCondition('Very Good')}
              >
                {t.collectionPage.veryGood}
              </Badge>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, idx) => (
                <div key={idx} className="animate-pulse">
                  <div className="bg-gray-200 h-72 rounded-t-lg"></div>
                  <div className="bg-white p-4 rounded-b-lg">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <Card 
                  key={product.id} 
                  className="card group overflow-hidden border-0 cursor-pointer"
                  style={{ 
                    backgroundColor: 'white',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    borderRadius: '8px'
                  }}
                  onClick={() => {
                    setSelectedProduct(product);
                    setIsDetailModalOpen(true);
                  }}
                >
                  <div className="relative overflow-hidden bg-gray-100">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                        style={{ contentVisibility: 'auto' }}
                      />
                    ) : (
                      <div className="w-full h-72 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                        <ShoppingBag size={64} style={{ color: 'var(--warm-gray)', opacity: 0.3 }} />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToWishlist(product.id);
                      }}
                      className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                    >
                      <Heart className="w-5 h-5" style={{ color: 'var(--warm-gray)' }} />
                    </button>
                    <Badge 
                      className="absolute top-4 left-4"
                      style={{ backgroundColor: 'var(--warm-sage)', color: 'var(--cream-white)' }}
                    >
                      {product.condition}
                    </Badge>
                    {product.featured && (
                      <Badge 
                        className="absolute bottom-4 left-4"
                        style={{ backgroundColor: 'var(--gold-accent)', color: 'var(--charcoal)' }}
                      >
                        {t.collectionPage.featured}
                      </Badge>
                    )}
                  </div>
                  
                  <CardContent className="p-6">
                    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                      {product.category}
                    </p>
                    <h3 className="text-lg font-medium mb-3 line-clamp-2" style={{ color: 'var(--charcoal)', minHeight: '56px' }}>
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-2xl font-semibold" style={{ color: 'var(--lv-brown)' }}>
                          CHF {product.price}
                        </p>
                      </div>
                      <Button 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProduct(product.name);
                          setIsContactModalOpen(true);
                        }}
                        style={{ backgroundColor: 'var(--warm-sage)', color: 'var(--cream-white)' }}
                      >
                        {t.product.contactUs}
                      </Button>
                    </div>
                    <Button 
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/payment?product=${encodeURIComponent(product.name)}&price=${product.price}`);
                      }}
                      style={{ backgroundColor: 'var(--lv-brown)', color: 'var(--cream-white)' }}
                    >
                      {language === 'de' ? 'Kaufen' : 'Buy Now'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <SlidersHorizontal className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--warm-gray)' }} />
              <h3 className="text-2xl font-light mb-2" style={{ color: 'var(--charcoal)' }}>
                {t.collectionPage.noProducts}
              </h3>
              <p style={{ color: 'var(--warm-gray)' }}>
                {t.collectionPage.noProductsDesc}
              </p>
              <Button 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedCondition('all');
                }}
                className="mt-6"
                style={{ backgroundColor: 'var(--warm-sage)', color: 'var(--cream-white)' }}
              >
                {t.collectionPage.clearFilters}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          setSelectedProduct(null);
        }}
        productName={typeof selectedProduct === 'string' ? selectedProduct : selectedProduct?.name}
      />

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedProduct(null);
        }}
        onContactClick={(product) => {
          setSelectedProduct(product.name);
          setIsContactModalOpen(true);
        }}
        language={language}
      />
    </div>
  );
};

export default CollectionPage;